// Pure presentation helpers for the Intern Admin Console.
import type { TaskStatus } from '@features/intern/types';
import type { TaskNote, AdminTask } from './types';

export const STATUS_LABEL: Record<TaskStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};

// Maps to @/components/ui/badge variants
export const STATUS_BADGE: Record<TaskStatus, 'secondary' | 'info' | 'destructive' | 'success'> = {
  not_started: 'secondary',
  in_progress: 'info',
  blocked: 'destructive',
  done: 'success',
};

export const TASK_STATUSES: TaskStatus[] = ['not_started', 'in_progress', 'blocked', 'done'];

// Statuses shown on the admin "Tasks" (active) tab — "done" lives in its own tab.
export const ACTIVE_TASK_STATUSES: TaskStatus[] = TASK_STATUSES.filter((s) => s !== 'done');

// Split tasks into active (not done) vs done, preserving input order.
export function partitionByDone<T extends { status: TaskStatus }>(
  tasks: T[],
): { active: T[]; done: T[] } {
  const active: T[] = [];
  const done: T[] = [];
  for (const t of tasks) {
    (t.status === 'done' ? done : active).push(t);
  }
  return { active, done };
}

// A note is an open blocker when it is flagged as a blocker and not yet resolved.
export function isOpenBlocker(note: Pick<TaskNote, 'is_blocker' | 'resolved'>): boolean {
  return note.is_blocker && !note.resolved;
}

// Short date label, "—" when absent.
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Human-friendly due text derived from the view's due_in_days.
export function dueLabel(task: Pick<AdminTask, 'due_date' | 'due_in_days' | 'status'>): string {
  if (!task.due_date) return 'No due date';
  if (task.status === 'done') return formatDate(task.due_date);
  const d = task.due_in_days;
  if (d == null) return formatDate(task.due_date);
  if (d < 0) return `${Math.abs(d)}d overdue`;
  if (d === 0) return 'Due today';
  if (d === 1) return 'Due tomorrow';
  return `Due in ${d}d`;
}

// Tailwind text color for the escalation state used on the due cell.
export function escalationClass(escalation: AdminTask['escalation']): string {
  switch (escalation) {
    case 'red':
      return 'text-destructive font-medium';
    case 'orange':
      return 'text-warning font-medium';
    default:
      return 'text-muted-foreground';
  }
}
